import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IPaginationProps,
    NCoreUIKitTheme,
    Pagination,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Pagination";

const USAGE_CODE = `const [page, setPage] = useState(1);

<Pagination
    onPageChange={setPage}
    totalItems={482}
    pageSize={20}
    page={page}
/>`;

const CURSOR_CODE = `<Pagination
    hasNextPage={data.hasNextPage}
    onPageChange={setPage}
    page={page}
/>`;

const LINKS_CODE = `<Pagination
    hrefExtractor={(target) => \`/reports?page=\${target}\`}
    onPageChange={handlePageChange}
    totalItems={482}
    pageSize={20}
    page={page}
/>`;

const LOAD_MORE_CODE = `<Pagination
    loadedCount={items.length}
    onLoadMore={fetchNextPage}
    isLoading={isFetching}
    variant="loadMore"
    totalItems={total}
    unitTitle="reports"
    isError={isError}
/>`;

const TABLE_CODE = `<Pagination
    onPageSizeChange={setPageSize}
    onPageChange={setPage}
    totalItems={482}
    pageSize={pageSize}
    page={page}
    size="small"
>
    <Pagination.Range
        unitTitle="reports"
    />
    <Pagination.PageSize
        options={[10, 20, 50]}
    />
    <Pagination.Content />
</Pagination>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const TABLE_PAGE_SIZES = [
    10,
    20,
    50
];

const TOTAL_ITEMS = 482;

const PAGE_SIZE = 20;

const INITIAL_PAGE = 13;

const CURSOR_LAST_PAGE = 6;

const LOAD_STEP = 20;

const LOAD_TOTAL = 60;

const LOAD_DELAY = 900;

const TABLE_INITIAL_PAGE = 3;

const TABLE_INITIAL_PAGE_SIZE = 10;

type DemoPaginationProps = Omit<IPaginationProps, "onPageChange" | "page">;

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: "100%",
    gap: 8
};

const CENTERED_STYLE: IPaginationProps["style"] = {
    justifyContent: "center"
};

const useStageWidthStyle = (): IPaginationProps["style"] => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    return {
        maxWidth: shapeConfigs.compactBreakpoint,
        alignSelf: "center",
        width: "100%"
    };
};

const DemoPagination = (props: DemoPaginationProps) => {
    const stageWidthStyle = useStageWidthStyle();

    const [
        page,
        setPage
    ] = useState(INITIAL_PAGE);

    return <Pagination
        totalItems={TOTAL_ITEMS}
        pageSize={PAGE_SIZE}
        style={[
            CENTERED_STYLE,
            stageWidthStyle
        ]}
        {...props}
        onPageChange={setPage}
        page={page}
    />;
};

const CursorPagination = () => {
    const stageWidthStyle = useStageWidthStyle();

    const [
        page,
        setPage
    ] = useState(1);

    return <Pagination
        hasNextPage={page < CURSOR_LAST_PAGE}
        onPageChange={setPage}
        style={[
            CENTERED_STYLE,
            stageWidthStyle
        ]}
        page={page}
    />;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("paginationPage.optionsTitle"),
        id: "options"
    },
    {
        title: t("paginationPage.loadMoreTitle"),
        id: "loadMore"
    },
    {
        title: t("paginationPage.tableTitle"),
        id: "table"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const PaginationPage = () => {
    const {
        t
    } = useDocsI18n();

    const stageWidthStyle = useStageWidthStyle();

    const [
        page,
        setPage
    ] = useState(INITIAL_PAGE);

    const [
        loadedCount,
        setLoadedCount
    ] = useState(LOAD_STEP);

    const [
        isLoadingMore,
        setIsLoadingMore
    ] = useState(false);

    const [
        tablePage,
        setTablePage
    ] = useState(TABLE_INITIAL_PAGE);

    const [
        tablePageSize,
        setTablePageSize
    ] = useState(TABLE_INITIAL_PAGE_SIZE);

    useEffect(() => {
        if(!isLoadingMore) {
            return;
        }

        const timer = window.setTimeout(() => {
            setLoadedCount((count) => Math.min(count + LOAD_STEP, LOAD_TOTAL));
            setIsLoadingMore(false);
        }, LOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoadingMore
    ]);

    const handleLoadMore = () => setIsLoadingMore(true);

    const handleStartOver = () => setLoadedCount(LOAD_STEP);

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("paginationPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("paginationPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("paginationPage.overviewControlled")}
            </Paragraph>
            <Preview
                caption={t("paginationPage.overviewCaption", [
                    page
                ])}
            >
                <div
                    style={CELL_STYLE}
                >
                    <Pagination
                        totalItems={TOTAL_ITEMS}
                        onPageChange={setPage}
                        pageSize={PAGE_SIZE}
                        style={[
                            CENTERED_STYLE,
                            stageWidthStyle
                        ]}
                        page={page}
                    />
                </div>
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("paginationPage.stageNote")}
            </Paragraph>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("paginationPage.variantsText")}
            </Paragraph>
            <Preview>
                {renderCell("variant=\"numbered\"", <DemoPagination />)}
                {
                    renderCell("variant=\"compact\"", <DemoPagination
                        variant="compact"
                    />)
                }
                {renderCell("hasNextPage", <CursorPagination />)}
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("paginationPage.cursorText")}
            </Paragraph>
            <CodeBlock
                code={CURSOR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("paginationPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => renderCell(`size="${size}"`, <DemoPagination
                        size={size}
                    />))
                }
            </Preview>
        </Section>
        <Section
            title={t("paginationPage.optionsTitle")}
            id="options"
        >
            <Paragraph>
                {t("paginationPage.optionsText")}
            </Paragraph>
            <Preview>
                {
                    renderCell("siblingCount={2}", <DemoPagination
                        siblingCount={2}
                    />)
                }
                {
                    renderCell("boundaryCount={2}", <DemoPagination
                        boundaryCount={2}
                    />)
                }
                {
                    renderCell("stepTitleBehaviour=\"hidden\"", <DemoPagination
                        stepTitleBehaviour="hidden"
                    />)
                }
                {
                    renderCell("isDisabled={true}", <DemoPagination
                        isDisabled={true}
                    />)
                }
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("paginationPage.linksText")}
            </Paragraph>
            <CodeBlock
                code={LINKS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("paginationPage.loadMoreTitle")}
            id="loadMore"
        >
            <Paragraph>
                {t("paginationPage.loadMoreText")}
            </Paragraph>
            <Preview
                caption={t("paginationPage.loadMoreCaption", [
                    LOAD_STEP,
                    LOAD_TOTAL
                ])}
            >
                {
                    renderCell("variant=\"loadMore\"", <Pagination
                        unitTitle={t("paginationPage.unit")}
                        onLoadMore={handleLoadMore}
                        isLoading={isLoadingMore}
                        loadedCount={loadedCount}
                        totalItems={LOAD_TOTAL}
                        variant="loadMore"
                    />)
                }
                {
                    renderCell("isError={true}", <Pagination
                        errorTitle={t("paginationPage.errorTitle")}
                        unitTitle={t("paginationPage.unit")}
                        onLoadMore={() => undefined}
                        totalItems={LOAD_TOTAL}
                        loadedCount={LOAD_STEP}
                        variant="loadMore"
                        isError={true}
                    />)
                }
                <Button
                    isDisabled={loadedCount === LOAD_STEP || isLoadingMore}
                    title={t("paginationPage.startOver")}
                    onPress={handleStartOver}
                    spreadBehaviour="free"
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={LOAD_MORE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("paginationPage.tableTitle")}
            id="table"
        >
            <Paragraph>
                {t("paginationPage.tableText")}
            </Paragraph>
            <Preview
                caption={t("paginationPage.tableCaption")}
            >
                <div
                    style={CELL_STYLE}
                >
                    <Pagination
                        unitTitle={t("paginationPage.unit")}
                        onPageSizeChange={setTablePageSize}
                        pageSizeOptions={TABLE_PAGE_SIZES}
                        onPageChange={setTablePage}
                        pageSize={tablePageSize}
                        totalItems={TOTAL_ITEMS}
                        style={stageWidthStyle}
                        page={tablePage}
                        size="small"
                    >
                        <Pagination.Range />
                        <Pagination.PageSize />
                        <Pagination.Content />
                    </Pagination>
                </div>
            </Preview>
            <CodeBlock
                code={TABLE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("paginationPage.a11yLandmark"),
                    t("paginationPage.a11yList"),
                    t("paginationPage.a11yCurrent"),
                    t("paginationPage.a11yStep"),
                    t("paginationPage.a11yKeyboard"),
                    t("paginationPage.a11yLinks"),
                    t("paginationPage.a11yLive"),
                    t("paginationPage.a11yEllipsis"),
                    t("paginationPage.a11yPageSize")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("paginationPage.propPage"),
                        defaultValue: "1",
                        type: "number",
                        name: "page"
                    },
                    {
                        description: t("paginationPage.propPageSize"),
                        defaultValue: "20",
                        name: "pageSize",
                        type: "number"
                    },
                    {
                        description: t("paginationPage.propTotalItems"),
                        name: "totalItems",
                        type: "number"
                    },
                    {
                        description: t("paginationPage.propOnPageChange"),
                        type: "(page: number) => void",
                        name: "onPageChange"
                    },
                    {
                        description: t("paginationPage.propOnPageSizeChange"),
                        type: "(pageSize: number) => void",
                        name: "onPageSizeChange"
                    },
                    {
                        type: "\"numbered\" | \"compact\" | \"loadMore\"",
                        description: t("paginationPage.propVariant"),
                        defaultValue: "\"numbered\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("paginationPage.propSize"),
                        name: "size"
                    },
                    {
                        description: t("paginationPage.propSiblingCount"),
                        name: "siblingCount",
                        defaultValue: "1",
                        type: "number"
                    },
                    {
                        description: t("paginationPage.propBoundaryCount"),
                        name: "boundaryCount",
                        defaultValue: "1",
                        type: "number"
                    },
                    {
                        description: t("paginationPage.propStepTitleBehaviour"),
                        type: "\"auto\" | \"visible\" | \"hidden\"",
                        name: "stepTitleBehaviour",
                        defaultValue: "\"auto\""
                    },
                    {
                        description: t("paginationPage.propPageSizeOptions"),
                        defaultValue: "[10, 20, 50, 100]",
                        name: "pageSizeOptions",
                        type: "Array<number>"
                    },
                    {
                        description: t("paginationPage.propHrefExtractor"),
                        type: "(page: number) => string",
                        name: "hrefExtractor"
                    },
                    {
                        description: t("paginationPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propHasNextPage"),
                        name: "hasNextPage",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propLoadedCount"),
                        name: "loadedCount",
                        type: "number"
                    },
                    {
                        description: t("paginationPage.propOnLoadMore"),
                        type: "() => void",
                        name: "onLoadMore"
                    },
                    {
                        description: t("paginationPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propIsError"),
                        defaultValue: "false",
                        name: "isError",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propErrorTitle"),
                        name: "errorTitle",
                        type: "string"
                    },
                    {
                        description: t("paginationPage.propUnitTitle"),
                        name: "unitTitle",
                        type: "string"
                    },
                    {
                        description: t("paginationPage.propChildren"),
                        name: "children",
                        type: "ReactNode"
                    },
                    {
                        description: t("paginationPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("paginationPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("paginationPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("paginationPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Pagination.Content
                </InlineCode>
                {" "}
                {t("paginationPage.partContentText")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Pagination.Item
                </InlineCode>
                {" "}
                {t("paginationPage.partItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("paginationPage.propItemPage"),
                        type: "number",
                        name: "page"
                    },
                    {
                        description: t("paginationPage.propItemHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("paginationPage.propItemIsCurrent"),
                        name: "isCurrent",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propItemIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propItemOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Pagination.Previous
                </InlineCode>
                {" "}
                {t("paginationPage.conjunction")}
                {" "}
                <InlineCode>
                    Pagination.Next
                </InlineCode>
                {" "}
                {t("paginationPage.partStepText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("paginationPage.propStepTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("paginationPage.propStepIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("paginationPage.propStepOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Pagination.Status
                </InlineCode>
                {" "}
                {t("paginationPage.partStatusText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("paginationPage.propStatusTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Pagination.Range
                </InlineCode>
                {" "}
                {t("paginationPage.partRangeText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("paginationPage.propRangeUnitTitle"),
                        name: "unitTitle",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Pagination.PageSize
                </InlineCode>
                {" "}
                {t("paginationPage.partPageSizeText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("paginationPage.propPartSizeTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("paginationPage.propPartSizeOptions"),
                        type: "Array<number>",
                        name: "options"
                    },
                    {
                        description: t("paginationPage.propPartSizeOnChange"),
                        type: "(pageSize: number) => void",
                        name: "onChange"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("paginationPage.partOtherText")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default PaginationPage;
